import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Switch } from "@/components/ui/switch";
import { Eye, ArrowRightLeft, Timer } from "lucide-react";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import Paginat from "@/components/lib/paginat";
import { SwitchDeviceDialog } from "@/components/dialog/device-management/SwitchDeviceDialog";
import { TokenDialog } from "@/components/dialog/device-management/TokenDialog";
import { PaymentAlertTimeDialog } from "@/components/dialog/device-management/PaymentAlertTimeDialog";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import {
  fetchDeviceManagement,
  switchDeviceThunk,
  toggleDeviceStatusThunk,
  updatePaymentAlertTimeThunk,
  clearDeviceManagementMessages,
} from "@/redux/slice/settings/device-management-slice";
import hasPermission from "@/hooks/hasPermission";

// Zod schema for device form is now in AddEditDeviceDialog component

const formatDateTime = (value) => {
  if (!value) return "-";

  const normalized = String(value).includes("T")
    ? String(value)
    : String(value).replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) return String(value);

  return format(date, "dd/MM/yyyy hh:mm a");
};

export function DeviceManagement() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  // Dialog states
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [alertTimeDialogOpen, setAlertTimeDialogOpen] = useState(false);
  const [alertTimeDevice, setAlertTimeDevice] = useState(null);
  const [tokenDialogData, setTokenDialogData] = useState({
    deviceName: "",
    tokenType: "",
    token: "",
  });

  const {
    devices,
    getLoading: isLoading,
    pagination,
    error,
    acceptLoading,
    blockLoading,
    unblockLoading,
    switchLoading,
    toggleLoading,
    paymentAlertTimeLoading,
  } = useSelector((state) => state.settings.deviceManagement);

  const restaurantId = user?.restaurant_id;

  const rows = useMemo(
    () =>
      (devices || []).map((device) => ({
        id: device.id,
        deviceName: device.device_name || "-",
        deviceType: device.device_type || "-",
        tokenWeb: device.token_web || "-",
        tokenNative: device.token_native || "-",
        worksAsNative: device.works_as_native ? "Yes" : "No",
        parentDevice: device.parent_device?.device_name || "-",
        pexName: device.pex_name || "-",
        printer: device.printer?.device_name || "-",
        paymentAlertTime: device.payment_alert_time,
        dateTime: formatDateTime(device.updated_at || device.created_at),
        status: device.status || (device.is_active ? "active" : "inactive"),
        isBlocked: device.is_blocked || false,
        isActive: device.is_active || false,
        fullDevice: device,
      })),
    [devices],
  );

  const transferDeviceOptions = useMemo(
    () =>
      rows.map((row) => ({
        value: String(row?.id),
        label: row?.deviceName,
        deviceType: row?.deviceType,
      })),
    [rows],
  );

  const tillTransferOptions = useMemo(
    () =>
      (transferDeviceOptions || [])
        .filter((opt) => String(opt.deviceType || "").toLowerCase() === "till")
        .map(({ value, label }) => ({ value, label })),
    [transferDeviceOptions],
  );

  const openSwitchDialog = (device) => {
    setSelectedDevice(device.fullDevice || device);
    setSwitchDialogOpen(true);
  };

  const openAlertTimeDialog = (device) => {
    setAlertTimeDevice(device.fullDevice || device);
    setAlertTimeDialogOpen(true);
  };

  const confirmPaymentAlertTime = async (seconds) => {
    if (!alertTimeDevice?.id) return;

    const res = await dispatch(
      updatePaymentAlertTimeThunk({
        deviceId: alertTimeDevice.id,
        payment_alert_time: Number(seconds),
      }),
    );

    if (res.payload && !res.error) {
      toast.success("Payment alert time updated successfully");
      setAlertTimeDialogOpen(false);
      setAlertTimeDevice(null);
    } else {
      toast.error(res.payload || "Failed to update payment alert time");
    }
  };

  const openTokenDialog = ({ deviceName, tokenType, token }) => {
    setTokenDialogData({
      deviceName: deviceName || "",
      tokenType: tokenType || "web",
      token: token || "",
    });
    setTokenDialogOpen(true);
  };

  const confirmSwitch = async ({ targetDeviceId, note }) => {
    if (!selectedDevice?.id || !targetDeviceId || !restaurantId) return;

    const res = await dispatch(
      switchDeviceThunk({
        restaurantId,
        deviceId: selectedDevice.id,
        target_device_id: Number(targetDeviceId),
        note,
      }),
    );

    if (res.payload && !res.error) {
      toast.success("Device switched successfully");
      setSwitchDialogOpen(false);
      setSelectedDevice(null);
    } else {
      toast.error(res.payload || "Failed to switch device");
    }
  };

  const handleToggleStatus = async (deviceId) => {
    if (!deviceId) return;

    const res = await dispatch(toggleDeviceStatusThunk(deviceId));

    if (res.payload && !res.error) {
      toast.success("Device status updated successfully");
    } else {
      toast.error(res.payload || "Failed to update device status");
    }
  };

  const handlePageChange = useCallback(
    (newPage) => {
      if (!restaurantId) return;
      setCurrentPage(newPage);
    },
    [restaurantId],
  );

  useEffect(() => {
    if (!restaurantId) return;

    dispatch(
      fetchDeviceManagement({
        restaurantId,
        page: currentPage,
        per_page: perPage,
      }),
    );
  }, [dispatch, restaurantId, currentPage, perPage]);

  useEffect(() => {
    if (!error) return;

    toast.error(typeof error === "string" ? error : "Something went wrong");
    dispatch(clearDeviceManagementMessages());
  }, [error, dispatch]);

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Device Management" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Settings", path: "/backoffice/settings" },
        {
          name: "Device Management",
          path: "/backoffice/settings/device-management",
        },
      ]}
    >
      <div className="space-y-6 container">
        <div className="mt-3 flex justify-between items-center">
          {/* <h3 className="text-md font-medium">Device Onboarding</h3> */}
        </div>
        <div className="bg-card rounded-md overflow-hidden shadow-sm">
          <Table>
            <TableHeader className="bg-accent/30">
              <TableRow className="border-border/50 hover:bg-transparent">
                <TableHead className="">Device Name</TableHead>
                <TableHead className="">Device Type</TableHead>
                <TableHead className="">Token (Web)</TableHead>
                <TableHead className="">Token (Native)</TableHead>
                <TableHead className="">Work as Native</TableHead>
                <TableHead className="">Parent Device</TableHead>
                <TableHead className="">PEX Name</TableHead>
                <TableHead className="">Printer</TableHead>
                <TableHead className="">Payment Alert Time</TableHead>
                <TableHead className="">Date and Time</TableHead>
                <TableHead className="">Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell colSpan={12}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
                : null}

              {!isLoading && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    className="text-muted-foreground text-sm"
                  >
                    No devices found.
                  </TableCell>
                </TableRow>
              ) : null}

              {!isLoading
                ? rows.map((row) => {
                    const isPaxTerminal = String(row?.deviceType || "").toLowerCase() === "pax_terminal";
                    return (
                      <TableRow
                        key={row?.id}
                        className="border-border/50 transition-all hover:bg-accent/5"
                      >
                        <TableCell>{row?.deviceName || "-"}</TableCell>
                        <TableCell className="capitalize">
                          {row?.deviceType || "-"}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() =>
                              openTokenDialog({
                                deviceName: row?.deviceName,
                                tokenType: "web",
                                token: row?.tokenWeb,
                              })
                            }
                            aria-label={`View ${row?.deviceName} web token`}
                          >
                            <Eye className="text-muted-foreground" />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() =>
                              openTokenDialog({
                                deviceName: row?.deviceName,
                                tokenType: "native",
                                token: row?.tokenNative,
                              })
                            }
                            aria-label={`View ${row?.deviceName} native token`}
                          >
                            <Eye className="text-muted-foreground" />
                          </Button>
                        </TableCell>
                        <TableCell>{row?.worksAsNative || "-"}</TableCell>
                        <TableCell>{row?.parentDevice || "-"}</TableCell>
                        <TableCell>{row?.pexName || "-"}</TableCell>
                        <TableCell>{row?.printer || "-"}</TableCell>
                        <TableCell>
                          {isPaxTerminal ? (
                            <button
                              type="button"
                              onClick={() => openAlertTimeDialog(row)}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-sm font-medium bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                              title="Click to edit payment alert time"
                            >
                              <Timer size={13} />
                              {row?.fullDevice?.payment_alert_time ?? 0}s
                            </button>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>{row?.dateTime || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                        {
                          hasPermission("back_office.settings.device_toggle_status") && (<Switch
                            checked={
                              String(row?.status || "").toLowerCase() ===
                              "active"
                            }
                            onClick={() => handleToggleStatus(row?.id)}
                            disabled={toggleLoading}
                          />)
                        }
                        <span className="capitalize text-sm">
                          {row?.status}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell className="text-right text-muted-foreground">
                      <span className="flex justify-end items-center gap-1">
                        {isPaxTerminal ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            title="Set payment alert time"
                            onClick={() => openAlertTimeDialog(row)}
                            aria-label={`Set payment alert time for ${row?.deviceName}`}
                          >
                            <Timer className="text-yellow-500 hover:text-yellow-400" size={16} />
                          </Button>
                        ) : null}
                        {String(row?.deviceType || "").toLowerCase() ===
                          "tab" ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            title="Transfer device"
                            onClick={() => openSwitchDialog(row)}
                            aria-label={`Transfer ${row?.deviceName}`}
                          >
                            <ArrowRightLeft className="text-muted-foreground" />
                          </Button>
                        ) : null}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
                : null}
            </TableBody>
          </Table>
          <Paginat
            className="justify-end py-4"
            pagination={pagination}
            onPageChange={handlePageChange}
          />

          <SwitchDeviceDialog
            isOpen={switchDialogOpen}
            onOpenChange={setSwitchDialogOpen}
            deviceName={
              selectedDevice?.device_name || selectedDevice?.deviceName || ""
            }
            transferDeviceOptions={tillTransferOptions.filter(
              (option) => option?.value !== String(selectedDevice?.id ?? ""),
            )}
            onConfirm={confirmSwitch}
            loading={switchLoading}
          />

          <TokenDialog
            isOpen={tokenDialogOpen}
            onOpenChange={setTokenDialogOpen}
            deviceName={tokenDialogData.deviceName}
            tokenType={tokenDialogData.tokenType}
            token={tokenDialogData.token}
          />

          <PaymentAlertTimeDialog
            isOpen={alertTimeDialogOpen}
            onOpenChange={setAlertTimeDialogOpen}
            deviceName={
              alertTimeDevice?.device_name || alertTimeDevice?.deviceName || ""
            }
            initialAlertTime={alertTimeDevice?.payment_alert_time ?? 30}
            onConfirm={confirmPaymentAlertTime}
            loading={paymentAlertTimeLoading}
          />
        </div>
      </div>
    </Layout2>
  );
}

export default DeviceManagement;
